/**
 * パルタウンの3キャラの絵(docs/14_characters.md §2)。
 *
 * 絵の実体は **`public/assets/img/chars/{キャラ}-{表情}.webp`(512px 透過)**。
 * 元データは `design/characters/sprites/`(1024px)で、
 * `node design/characters/export-sprites.mjs` が 512px に落として書き出す。
 *
 * 境界はここで閉じてある:
 *   - 外に出るのは `<CharacterArt speaker mood pose size />` **1つだけ**
 *   - シナリオ側の語彙(`LessonMood` / `LessonPose`)は `@paltyping/quiz` が持つ。
 *     絵の都合で語彙を増やさない(増やすとシナリオを書き直すことになる)
 *
 * **SVG も残してある**。消さない理由は2つ:
 *   1. パルの体色反映(docs/14 §2 の色システム)。スプライトは白ボディの写真なので、
 *      CSS フィルタで色を付けると線や ほっぺ まで染まって汚くなる。
 *      **色が付くパルのときだけ SVG**(塗り分けができる)に切り替える
 *   2. 画像が読めないとき(オフライン初回など)のフォールバック
 *
 * 大小関係(ポカ=パルの半分。docs/14 §2)の持ちかた:
 *   - SVG は 100×100 の座標系の中の身体の大きさで表す
 *   - スプライトは書き出しの時点でどのキャラも「枠いっぱい」に正規化されているので、
 *     **`SPRITE_SCALE` の倍率**で表す。どちらも呼び出し側は同じ `size` を渡すだけでよい
 */

import { useId, useState, type ReactNode } from 'react';
import type { LessonMood, LessonPose, LessonSpeaker } from '@paltyping/quiz';
import { PAL_WHITE, type PalPalette } from '../../logic/palColor';
import '../components/gameFeel.css';

/** 線の色(3キャラ共通。フラット単色)。 */
const INK = '#2b3440';

/** 体色。docs/14 §2 の指定色。 */
const PAL_BODY = '#ffffff';
const OCTO_BODY = '#ef7a85';
const POKA_BODY = '#b8a6dd';

/** 絵を持つキャラの表示名(読み上げ・aria-label 用)。 */
export const CHARACTER_NAMES: Readonly<Record<'pal' | 'octo' | 'poka', string>> = {
  pal: 'パル',
  octo: 'オクトせんせい',
  poka: 'ポカ',
};

interface FaceProps {
  readonly mood: LessonMood;
  /** 目の中心(左右)と口の位置。 */
  readonly eyeY: number;
  readonly eyeGap: number;
  readonly eyeR: number;
  readonly mouthY: number;
  readonly cx: number;
}

/**
 * 表情(3キャラ共通の描きかた)。
 *
 * `normal` = まるい目 + 小さい口 / `happy` = 弧の目(^^) + 大きい口 /
 * `puzzled` = まるい目 + ハの字まゆ + 小さい口。
 * 目と口だけで作るので、体のかたちが変わっても同じ関数で載せられる。
 */
function Face({ mood, eyeY, eyeGap, eyeR, mouthY, cx }: FaceProps) {
  const left = cx - eyeGap / 2;
  const right = cx + eyeGap / 2;
  const stroke = eyeR * 0.7;

  return (
    <>
      {mood === 'happy' ? (
        <>
          {/* よろこび: 目を伏せた弧(^ ^)。 */}
          <path
            d={`M ${left - eyeR} ${eyeY + eyeR * 0.4} Q ${left} ${eyeY - eyeR} ${left + eyeR} ${eyeY + eyeR * 0.4}`}
            fill="none"
            stroke={INK}
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          <path
            d={`M ${right - eyeR} ${eyeY + eyeR * 0.4} Q ${right} ${eyeY - eyeR} ${right + eyeR} ${eyeY + eyeR * 0.4}`}
            fill="none"
            stroke={INK}
            strokeWidth={stroke}
            strokeLinecap="round"
          />
        </>
      ) : (
        <>
          <circle cx={left} cy={eyeY} r={eyeR} fill={INK} />
          <circle cx={right} cy={eyeY} r={eyeR} fill={INK} />
        </>
      )}

      {mood === 'puzzled' ? (
        <>
          {/* こまり: ハの字のまゆ。目より上に置くだけで「困った」に見える。 */}
          <path
            d={`M ${left - eyeR * 1.4} ${eyeY - eyeR * 2.4} L ${left + eyeR * 1.2} ${eyeY - eyeR * 1.4}`}
            stroke={INK}
            strokeWidth={stroke * 0.8}
            strokeLinecap="round"
          />
          <path
            d={`M ${right + eyeR * 1.4} ${eyeY - eyeR * 2.4} L ${right - eyeR * 1.2} ${eyeY - eyeR * 1.4}`}
            stroke={INK}
            strokeWidth={stroke * 0.8}
            strokeLinecap="round"
          />
        </>
      ) : null}

      {mood === 'happy' ? (
        // よろこび: 大きくあけた口。
        <path
          d={`M ${cx - eyeGap * 0.4} ${mouthY} a ${eyeGap * 0.4} ${eyeGap * 0.34} 0 0 0 ${eyeGap * 0.8} 0 z`}
          fill={INK}
        />
      ) : mood === 'puzzled' ? (
        // こまり: 小さくすぼめた口。
        <ellipse cx={cx} cy={mouthY + 1} rx={eyeR * 0.9} ry={eyeR * 0.9} fill={INK} />
      ) : (
        // ふつう: にっと結んだ口。
        <path
          d={`M ${cx - eyeGap * 0.28} ${mouthY} q ${eyeGap * 0.28} ${eyeGap * 0.22} ${eyeGap * 0.56} 0`}
          fill="none"
          stroke={INK}
          strokeWidth={stroke}
          strokeLinecap="round"
        />
      )}
    </>
  );
}

/** パルの体(正円)の中心と半径。塗り分けの座標をここから作る。 */
const PAL_BODY_CX = 50;
const PAL_BODY_CY = 56;
const PAL_BODY_R = 32;

/**
 * パル(主役マスコット)。
 * 白いもちもちの正円 + アホ毛1本。不透明(docs/14 §2)。
 *
 * 体色は「直近の取り組み量の比率で色が混ざる」(docs/14 §2)。
 * 色の計算は `logic/palColor.ts` にあり、ここは**塗るだけ**:
 *   - `solid`  … 円をその色で塗る
 *   - `rainbow`… 円で切り抜いた中に**縦の帯を等分**に並べる。
 *     グラデーションは使わない(CLAUDE.md ルール7)= フラット色の面分割
 */
function PalArt({
  mood,
  palette,
}: {
  readonly mood: LessonMood;
  readonly palette: PalPalette | undefined;
}) {
  // SVG の参照は id なので、同じ画面に2匹いても衝突しないように一意にする
  // (`useId` はコロンを含むので、fragment 参照に使えるよう取り除く)。
  const clipId = `pal-body-${useId().replace(/:/g, '')}`;
  const rainbow = palette?.kind === 'rainbow' ? palette.colors : null;
  const solid = palette?.kind === 'solid' ? palette.color : PAL_BODY;

  return (
    <>
      {/* アホ毛。ここが1本あるだけで「ロボでも動物でもない ふしぎ生き物」に見える。 */}
      <path
        d="M 50 22 q 4 -12 12 -14"
        fill="none"
        stroke={INK}
        strokeWidth="4"
        strokeLinecap="round"
      />
      {rainbow && rainbow.length > 0 ? (
        <>
          <clipPath id={clipId}>
            <circle cx={PAL_BODY_CX} cy={PAL_BODY_CY} r={PAL_BODY_R} />
          </clipPath>
          <g clipPath={`url(#${clipId})`}>
            {rainbow.map((color, index) => (
              <rect
                key={color + String(index)}
                x={PAL_BODY_CX - PAL_BODY_R + (index * (PAL_BODY_R * 2)) / rainbow.length}
                y={PAL_BODY_CY - PAL_BODY_R}
                // 帯どうしのすき間(丸め誤差の白線)が出ないよう少しだけ重ねる。
                width={(PAL_BODY_R * 2) / rainbow.length + 0.6}
                height={PAL_BODY_R * 2}
                fill={color}
              />
            ))}
          </g>
          <circle
            cx={PAL_BODY_CX}
            cy={PAL_BODY_CY}
            r={PAL_BODY_R}
            fill="none"
            stroke={INK}
            strokeWidth="4"
          />
        </>
      ) : (
        <circle
          cx={PAL_BODY_CX}
          cy={PAL_BODY_CY}
          r={PAL_BODY_R}
          fill={solid}
          stroke={INK}
          strokeWidth="4"
        />
      )}
      <Face mood={mood} cx={50} eyeY={50} eyeGap={26} eyeR={4.6} mouthY={64} />
    </>
  );
}

/**
 * オクト先生(解説役)。
 * タコの博士。8本腕(8教科・8パズルの象徴)と めがね(docs/14 §2)。
 */
function OctoArt({ mood }: { readonly mood: LessonMood }) {
  // 8本の足を等間隔に散らす。1本ずつ書かないのは、本数が象徴そのものだから
  // (数を変えたくなったらここだけを直す)。
  const legs = Array.from({ length: 8 }, (_, i) => {
    const x = 18 + i * 9.2;
    const curl = i % 2 === 0 ? 6 : -6;
    return (
      <path
        key={i}
        d={`M ${x} 62 q ${curl} 14 0 24`}
        fill="none"
        stroke={OCTO_BODY}
        strokeWidth="6"
        strokeLinecap="round"
      />
    );
  });

  return (
    <>
      {legs}
      <path
        d="M 16 58 a 34 32 0 0 1 68 0 z"
        fill={OCTO_BODY}
        stroke={INK}
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <Face mood={mood} cx={50} eyeY={40} eyeGap={28} eyeR={4.4} mouthY={54} />
      {/* めがね(博士のしるし)。目のまわりを丸で囲むだけ。 */}
      <g fill="none" stroke={INK} strokeWidth="2.6">
        <circle cx="36" cy="40" r="10" />
        <circle cx="64" cy="40" r="10" />
        <path d="M 46 40 h 8" />
      </g>
    </>
  );
}

/**
 * ポカ(まちがい役)。
 * うすむらさきで**半透明**の小さいおばけ。しっぽがふにゃっと曲がる。
 * **パルの半分サイズ**(白にしないのは初期パルとの被り回避。docs/14 §2)。
 */
function PokaArt({ mood }: { readonly mood: LessonMood }) {
  return (
    // 半透明はグループの opacity で出す(グラデーションは使わない)。
    <g opacity="0.78">
      <path
        d={
          // 頭は半円、すそは波、しっぽは右下へふにゃっと。
          'M 34 66 a 16 16 0 0 1 32 0 v 10 ' +
          'q -3 5 -6 0 q -3 5 -6 0 q -3 5 -6 0 q -3 5 -6 0 q -4 -2 -8 2 z'
        }
        fill={POKA_BODY}
        stroke={INK}
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <Face mood={mood} cx={50} eyeY={62} eyeGap={13} eyeR={2.4} mouthY={70} />
    </g>
  );
}

export interface CharacterArtProps {
  /** 誰を描くか。`narrator`(語り)は絵を持たないので `null` を返す。 */
  readonly speaker: LessonSpeaker;
  readonly mood?: LessonMood;
  /** 向き。`right` は左右反転(絵は1枚だけ持ち、反転で使い回す)。 */
  readonly pose?: LessonPose;
  /** 表示サイズ(px。正方形)。 */
  readonly size?: number;
  readonly className?: string;
  /**
   * パルの体色(docs/14 §2 の色システム)。**省略すると白いパル**。
   * `logic/palColor.ts` の `palColorOf()` が返す `palette` をそのまま渡す。
   * パル以外のキャラでは無視される。
   */
  readonly palette?: PalPalette;
  /**
   * 待機ループ(docs/15 §2D「静止画の瞬間がない」)。
   * ゆっくり上下に浮いて、ときどき まばたきのように縦につぶれる。
   *
   * **枠(コンテナ)に掛ける**ので、中身がスプライト画像でも SVG でも同じように効く。
   * 既定で on。動きを止めたい場面(印刷・並べて比べる図など)だけ `false` にする。
   * `prefers-reduced-motion: reduce` では CSS 側で自動的に止まる。
   */
  readonly idle?: boolean;
}

/** 絵を持つキャラ(= `narrator` を除いたもの)。 */
type DrawnSpeaker = 'pal' | 'octo' | 'poka';

/**
 * スプライトの表示倍率(`size` に対する割合)。
 *
 * 書き出し(`design/characters/export-sprites.mjs`)でどのキャラも
 * 「512 の枠に長辺いっぱい」へ正規化してあるので、**キャラどうしの大小関係はここだけ**で決まる。
 * ポカはパルの半分(docs/14 §2)。
 */
const SPRITE_SCALE: Readonly<Record<DrawnSpeaker, number>> = {
  pal: 1,
  octo: 1,
  poka: 0.5,
};

/** スプライトの URL。`public/` に置いてあるので、アプリと同じオリジンから出る。 */
function spriteUrl(speaker: DrawnSpeaker, mood: LessonMood): string {
  return `${import.meta.env.BASE_URL}assets/img/chars/${speaker}-${mood}.webp`;
}

/**
 * パルの体色が**白のまま**か。
 * 白ならスプライト(白ボディ)で足りる。色が付くときだけ塗り分けできる SVG を使う。
 */
function isWhitePal(palette: PalPalette | undefined): boolean {
  if (palette === undefined) return true;
  if (palette.kind === 'rainbow') return false;
  return palette.color.toLowerCase() === PAL_WHITE;
}

/**
 * 絵を置く枠。**中身が画像でも SVG でも枠の大きさは `size` 四方**にする
 * (ポカだけ絵が小さくても、まわりのレイアウトがずれない)。
 * 絵は枠の**下ぞろえ**。パルとポカが同じ地面に立って見える。
 *
 * `aria-hidden` にしてあるのは、**誰が話しているかは名前のテキストで出す**から
 * (絵を読み上げさせると「パル パル」と二重に読まれる)。
 */
function CharacterFrame({
  speaker,
  mood,
  pose,
  size,
  className,
  children,
}: {
  readonly speaker: DrawnSpeaker;
  readonly mood: LessonMood;
  readonly pose: LessonPose;
  readonly size: number;
  readonly className: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <span
      className={className}
      aria-hidden="true"
      data-character={speaker}
      data-mood={mood}
      data-pose={pose}
      style={{
        display: 'inline-grid',
        placeItems: 'end center',
        width: size,
        height: size,
        lineHeight: 0,
      }}
    >
      {children}
    </span>
  );
}

/**
 * スプライト1枚。読み込みに失敗したら**その場で SVG に落ちる**(オフライン初回など)。
 * 失敗した URL を覚えておくのは、表情が変わったら別ファイルなので改めて試すため。
 */
function CharacterSprite({
  speaker,
  mood,
  pose,
  size,
  className,
}: {
  readonly speaker: DrawnSpeaker;
  readonly mood: LessonMood;
  readonly pose: LessonPose;
  readonly size: number;
  readonly className: string | undefined;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = spriteUrl(speaker, mood);

  if (failedSrc === src) {
    return (
      <CharacterVector
        speaker={speaker}
        mood={mood}
        pose={pose}
        size={size}
        className={className}
      />
    );
  }

  const drawn = Math.round(size * SPRITE_SCALE[speaker]);
  return (
    <CharacterFrame speaker={speaker} mood={mood} pose={pose} size={size} className={className}>
      <img
        src={src}
        alt=""
        width={drawn}
        height={drawn}
        decoding="async"
        draggable={false}
        onError={() => setFailedSrc(src)}
        style={{
          display: 'block',
          width: drawn,
          height: drawn,
          // 反転の軸は絵の中心。ここで効かせておくと、呼び出し側の CSS が要らない。
          transform: pose === 'right' ? 'scaleX(-1)' : undefined,
        }}
      />
    </CharacterFrame>
  );
}

/**
 * 図形で描くほう。パルに色が付くときと、スプライトが読めないときに使う。
 * 100×100 の座標系の中に大小関係まで入っているので、枠いっぱいに置くだけでよい。
 */
function CharacterVector({
  speaker,
  mood,
  pose,
  size,
  className,
  palette,
}: {
  readonly speaker: DrawnSpeaker;
  readonly mood: LessonMood;
  readonly pose: LessonPose;
  readonly size: number;
  readonly className: string | undefined;
  readonly palette?: PalPalette | undefined;
}) {
  return (
    <CharacterFrame speaker={speaker} mood={mood} pose={pose} size={size} className={className}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="img"
        aria-hidden="true"
        style={pose === 'right' ? { transform: 'scaleX(-1)' } : undefined}
      >
        {speaker === 'pal' ? (
          <PalArt mood={mood} palette={palette} />
        ) : speaker === 'octo' ? (
          <OctoArt mood={mood} />
        ) : (
          <PokaArt mood={mood} />
        )}
      </svg>
    </CharacterFrame>
  );
}

/**
 * キャラ1人を描く。
 *
 * ふだんはスプライト画像。**パルに色が付いているときだけ**塗り分けのできる SVG
 * (スプライトは白ボディなので、CSS で色を乗せると線やほっぺまで染まる)。
 */
export function CharacterArt({
  speaker,
  mood = 'normal',
  pose = 'left',
  size = 96,
  className,
  palette,
  idle = true,
}: CharacterArtProps) {
  if (speaker === 'narrator') return null;

  /*
   * 待機ループは**枠に足すクラス1つ**で表す(docs/15 §2D)。
   * こうしておくと、下の2通りの描きかた(スプライト / SVG)のどちらでも
   * 同じ1本のアニメーションが掛かる = 見え方がぶれない。
   */
  const frameClass = [className, idle ? 'gf-idle' : ''].filter(Boolean).join(' ') || undefined;

  if (speaker === 'pal' && !isWhitePal(palette)) {
    return (
      <CharacterVector
        speaker={speaker}
        mood={mood}
        pose={pose}
        size={size}
        className={frameClass}
        palette={palette}
      />
    );
  }

  return (
    <CharacterSprite speaker={speaker} mood={mood} pose={pose} size={size} className={frameClass} />
  );
}
